// Hook global useDialog() unificando 3 padroes de feedback:
//
//   dialog.toast('msg', { tipo: 'sucesso' })            -> toast canto, nao-bloqueante
//   await dialog.alerta('msg', { titulo, tipo })         -> modal centralizado c/ OK
//   const ok = await dialog.confirmar('msg', { ... })    -> modal centralizado c/ Cancelar+OK
//
// Visual padronizado (compacto). Substitui ConfirmModal direto, alert(), confirm() nativos espalhados.
//
// Compat: useAlertModal() continua funcionando como alias pra dialog.alerta.

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { Modal } from './Modal';
import { useToast } from './Toast';

type TipoMsg = 'erro' | 'aviso' | 'info' | 'sucesso';

interface AlertaOpts { titulo?: string; tipo?: TipoMsg }
interface ConfirmarOpts {
  titulo?: string;
  textoConfirmar?: string;
  textoCancelar?: string;
  perigoso?: boolean;
  tipo?: TipoMsg;
  /** URL de uma foto exibida ao lado da mensagem (ex: foto do produto a excluir). */
  imagem?: string;
}

interface DialogCtx {
  toast: (mensagem: string, opts?: { tipo?: TipoMsg }) => void;
  alerta: (mensagem: string, opts?: AlertaOpts) => Promise<void>;
  confirmar: (mensagem: string, opts?: ConfirmarOpts) => Promise<boolean>;
}

interface AlertaState {
  aberto: boolean;
  mensagem: string;
  titulo?: string;
  tipo: TipoMsg;
  resolve?: () => void;
}

interface ConfirmState {
  aberto: boolean;
  mensagem: string;
  titulo?: string;
  textoConfirmar: string;
  textoCancelar: string;
  perigoso: boolean;
  tipo: TipoMsg;
  imagem?: string;
  resolve?: (v: boolean) => void;
}

const Ctx = createContext<DialogCtx | null>(null);

const COR: Record<TipoMsg, string> = {
  erro: '#dc2626',
  aviso: '#d97706',
  info: '#2563eb',
  sucesso: '#16a34a',
};

function tituloDefault(tipo: TipoMsg): string {
  return tipo === 'erro' ? 'Erro' : tipo === 'aviso' ? 'Atencao' : tipo === 'sucesso' ? 'Pronto' : 'Aviso';
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const toastRef = useToast();
  const [alerta, setAlerta] = useState<AlertaState>({ aberto: false, mensagem: '', tipo: 'info' });
  const [conf, setConf] = useState<ConfirmState>({
    aberto: false, mensagem: '', textoConfirmar: 'Confirmar', textoCancelar: 'Cancelar',
    perigoso: false, tipo: 'info',
  });

  const showToast = useCallback<DialogCtx['toast']>((mensagem, opts) => {
    const tipo = opts?.tipo ?? 'info';
    if (tipo === 'sucesso') toastRef.sucesso(mensagem);
    else if (tipo === 'erro') toastRef.erro(mensagem);
    else if (tipo === 'aviso') toastRef.aviso(mensagem);
    else toastRef.mostrar(mensagem, 'info');
  }, [toastRef]);

  const showAlerta = useCallback<DialogCtx['alerta']>((mensagem, opts) => {
    return new Promise<void>((resolve) => {
      setAlerta({
        aberto: true,
        mensagem,
        titulo: opts?.titulo,
        tipo: opts?.tipo ?? 'erro',
        resolve,
      });
    });
  }, []);

  const showConfirm = useCallback<DialogCtx['confirmar']>((mensagem, opts) => {
    return new Promise<boolean>((resolve) => {
      setConf({
        aberto: true,
        mensagem,
        titulo: opts?.titulo,
        textoConfirmar: opts?.textoConfirmar ?? 'Confirmar',
        textoCancelar: opts?.textoCancelar ?? 'Cancelar',
        perigoso: opts?.perigoso ?? false,
        tipo: opts?.tipo ?? 'info',
        imagem: opts?.imagem,
        resolve,
      });
    });
  }, []);

  function fecharAlerta() {
    alerta.resolve?.();
    setAlerta((s) => ({ ...s, aberto: false, resolve: undefined }));
  }

  function responderConfirm(valor: boolean) {
    conf.resolve?.(valor);
    setConf((s) => ({ ...s, aberto: false, resolve: undefined }));
  }

  return (
    <Ctx.Provider value={{ toast: showToast, alerta: showAlerta, confirmar: showConfirm }}>
      {children}

      {/* Alerta (so OK) */}
      <Modal
        aberto={alerta.aberto}
        onFechar={fecharAlerta}
        titulo={alerta.titulo ?? tituloDefault(alerta.tipo)}
        largura={560}
        compacto
        rodape={
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <button
              type="button" autoFocus onClick={fecharAlerta}
              style={{
                background: COR[alerta.tipo], color: '#fff', border: 0,
                padding: '8px 32px', borderRadius: 6, fontWeight: 600,
                cursor: 'pointer', fontSize: 14,
              }}
            >OK</button>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '4px 0' }}>
          <AlertTriangle size={28} style={{ color: COR[alerta.tipo], flexShrink: 0, marginTop: 2 }} />
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5, flex: 1 }}>
            {alerta.mensagem}
          </div>
        </div>
      </Modal>

      {/* Confirmar (Cancelar + OK/perigoso) */}
      <Modal
        aberto={conf.aberto}
        onFechar={() => responderConfirm(false)}
        titulo={conf.titulo ?? (conf.perigoso ? 'Confirmar' : 'Confirmacao')}
        largura={520}
        compacto
        rodape={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, width: '100%' }}>
            <button
              type="button"
              onClick={() => responderConfirm(false)}
              style={{
                background: '#e5e7eb', color: '#1f2937', border: 0,
                padding: '8px 24px', borderRadius: 6, fontWeight: 600,
                cursor: 'pointer', fontSize: 14,
              }}
            >{conf.textoCancelar}</button>
            <button
              type="button" autoFocus
              onClick={() => responderConfirm(true)}
              style={{
                background: conf.perigoso ? '#dc2626' : COR[conf.tipo === 'info' ? 'info' : conf.tipo],
                color: '#fff', border: 0,
                padding: '8px 24px', borderRadius: 6, fontWeight: 600,
                cursor: 'pointer', fontSize: 14,
              }}
            >{conf.textoConfirmar}</button>
          </div>
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: conf.imagem ? 'center' : 'flex-start',
            gap: 14,
            padding: '4px 0',
          }}
        >
          {conf.imagem ? (
            <img
              src={conf.imagem}
              alt=""
              style={{
                width: 84,
                height: 84,
                objectFit: 'contain',
                background: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                flexShrink: 0,
              }}
            />
          ) : conf.perigoso ? (
            <AlertTriangle size={28} style={{ color: '#dc2626', flexShrink: 0, marginTop: 2 }} />
          ) : (
            <HelpCircle size={28} style={{ color: COR[conf.tipo], flexShrink: 0, marginTop: 2 }} />
          )}
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5, flex: 1 }}>
            {conf.mensagem}
          </div>
        </div>
      </Modal>
    </Ctx.Provider>
  );
}

export function useDialog(): DialogCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useDialog fora de DialogProvider');
  return c;
}

// Compat: codigo existente que usa useAlertModal continua funcionando.
export function useAlertModal(): { alerta: DialogCtx['alerta'] } {
  const d = useDialog();
  return { alerta: d.alerta };
}
